/**
 * Trade journal store — the data layer behind the Journal panel.
 *
 * Phase 1: persisted in localStorage (per device). Phase 2 will sync this to
 * the backend per-account and add CSV / MetaApi (MT5) import — the Trade shape
 * here is already the canonical model those importers will write into.
 */

export type TradeDirection = 'long' | 'short';
export type TradeKind = 'real' | 'practice';

export interface Trade {
  id: string;
  kind: TradeKind;
  pair: string;
  direction: TradeDirection;
  entryPrice: number;
  exitPrice: number | null;        // null while the trade is still open
  size: number;                    // position size in lots
  /** Realised P&L in account currency, signed. null while open. */
  pnl: number | null;
  /** R-multiple (P&L ÷ risked amount). null when unknown / open. */
  rMultiple: number | null;
  openedAt: string;                // ISO timestamp
  closedAt: string | null;         // ISO timestamp, null while open
  tags: string[];
  rating: number;                  // execution quality 0–5 (0 = unrated)
  note: string;
  /** Optional screenshot URL (e.g. a shared TradingView chart image). */
  screenshot?: string | null;
}

const KEY_BASE = 'telos:journal:trades:v1';
/** Dispatched on any mutation so open views can refresh. */
export const TRADES_EVENT = 'telos:journal:trades-changed';

// Module-level current-user id. AuthContext calls setActiveUserId() whenever
// the authenticated user changes. All read/write functions build their
// localStorage key as `${KEY_BASE}:${activeUserId}` so trades imported by
// one TELOS account never appear under a different account on the same
// browser.
let activeUserId: string | null = null;
function tradesKey(): string {
  return activeUserId ? `${KEY_BASE}:${activeUserId}` : KEY_BASE;
}
function settingsKey(): string {
  return activeUserId ? `telos:journal:settings:v1:${activeUserId}` : 'telos:journal:settings:v1';
}

export function setActiveUserId(id: string | null) {
  const prev = activeUserId;
  activeUserId = id;
  if (prev === id) return;
  // No migration. Aggressively NUKE the legacy global keys whenever the
  // active user changes so they can never leak to another account on the
  // same browser. Anyone who imported under the old global key needs to
  // re-import once after this ships. Trade-off: guaranteed isolation.
  try {
    localStorage.removeItem(KEY_BASE);
    localStorage.removeItem('telos:journal:settings:v1');
  } catch {
    /* localStorage blocked — skip silently */
  }
  // Tell open views to re-read with the new scope.
  window.dispatchEvent(new Event(TRADES_EVENT));
}

/* ----------------------------------------------------------------------- */
/*  Storage                                                                */
/* ----------------------------------------------------------------------- */

export function readTrades(): Trade[] {
  try {
    const raw = localStorage.getItem(tradesKey());
    if (!raw) return [];
    const parsed = JSON.parse(raw);
    if (!Array.isArray(parsed)) return [];
    return parsed.filter((t): t is Trade => !!t && typeof t.id === 'string');
  } catch {
    return [];
  }
}

export function writeTrades(trades: Trade[]) {
  try {
    localStorage.setItem(tradesKey(), JSON.stringify(trades));
    window.dispatchEvent(new Event(TRADES_EVENT));
  } catch {
    /* quota or unavailable — ignore */
  }
}

function uid(): string {
  return `t_${Date.now().toString(36)}_${Math.random().toString(36).slice(2, 8)}`;
}

/** Newest first. */
function sortTrades(trades: Trade[]): Trade[] {
  return trades
    .slice()
    .sort((a, b) => new Date(b.openedAt).getTime() - new Date(a.openedAt).getTime());
}

export type NewTrade = Omit<Trade, 'id'>;

export function addTrade(t: NewTrade): Trade {
  const trade: Trade = { ...t, id: uid() };
  writeTrades(sortTrades([trade, ...readTrades()]));
  return trade;
}

export function updateTrade(id: string, patch: Partial<Trade>): void {
  writeTrades(
    sortTrades(readTrades().map((t) => (t.id === id ? { ...t, ...patch, id } : t))),
  );
}

export function deleteTrade(id: string): void {
  writeTrades(readTrades().filter((t) => t.id !== id));
}

export function clearTrades(): void {
  writeTrades([]);
}

/* ----------------------------------------------------------------------- */
/*  Analytics                                                              */
/* ----------------------------------------------------------------------- */

export interface JournalStats {
  total: number;
  closed: number;
  open: number;
  wins: number;
  losses: number;
  breakeven: number;
  winRate: number;          // %
  totalPnl: number;
  profitFactor: number;     // gross win ÷ gross loss
  expectancyR: number;      // average R per closed trade
  avgWin: number;
  avgLoss: number;
  avgWinR: number;
  avgLossR: number;
  bestPnl: number;
  worstPnl: number;
  /** Current consecutive win (+) or loss (−) streak. */
  streak: number;
}

const EMPTY_STATS: JournalStats = {
  total: 0, closed: 0, open: 0, wins: 0, losses: 0, breakeven: 0,
  winRate: 0, totalPnl: 0, profitFactor: 0, expectancyR: 0,
  avgWin: 0, avgLoss: 0, avgWinR: 0, avgLossR: 0,
  bestPnl: 0, worstPnl: 0, streak: 0,
};

export function computeStats(trades: Trade[]): JournalStats {
  const closed = trades.filter((t) => t.pnl != null && t.closedAt);
  if (!closed.length) {
    return { ...EMPTY_STATS, total: trades.length, open: trades.length };
  }

  const wins = closed.filter((t) => (t.pnl as number) > 0);
  const losses = closed.filter((t) => (t.pnl as number) < 0);
  const breakeven = closed.filter((t) => (t.pnl as number) === 0);

  const grossWin = wins.reduce((a, t) => a + (t.pnl as number), 0);
  const grossLoss = Math.abs(losses.reduce((a, t) => a + (t.pnl as number), 0));
  const totalPnl = closed.reduce((a, t) => a + (t.pnl as number), 0);

  const rValues = closed.map((t) => t.rMultiple).filter((r): r is number => r != null);
  const winR = wins.map((t) => t.rMultiple).filter((r): r is number => r != null);
  const lossR = losses.map((t) => t.rMultiple).filter((r): r is number => r != null);

  const avg = (arr: number[]) => (arr.length ? arr.reduce((a, b) => a + b, 0) / arr.length : 0);

  // Streak — walk closed trades newest-first until the sign flips.
  const chrono = closed
    .slice()
    .sort((a, b) => new Date(b.closedAt as string).getTime() - new Date(a.closedAt as string).getTime());
  let streak = 0;
  for (const t of chrono) {
    const p = t.pnl as number;
    if (p === 0) break;
    if (streak === 0) streak = p > 0 ? 1 : -1;
    else if ((p > 0 && streak > 0) || (p < 0 && streak < 0)) streak += p > 0 ? 1 : -1;
    else break;
  }

  return {
    total: trades.length,
    closed: closed.length,
    open: trades.length - closed.length,
    wins: wins.length,
    losses: losses.length,
    breakeven: breakeven.length,
    winRate: closed.length ? (wins.length / closed.length) * 100 : 0,
    totalPnl,
    profitFactor: grossLoss > 0 ? grossWin / grossLoss : grossWin > 0 ? Infinity : 0,
    expectancyR: avg(rValues),
    avgWin: avg(wins.map((t) => t.pnl as number)),
    avgLoss: avg(losses.map((t) => t.pnl as number)),
    avgWinR: avg(winR),
    avgLossR: avg(lossR),
    bestPnl: closed.reduce((m, t) => Math.max(m, t.pnl as number), -Infinity),
    worstPnl: closed.reduce((m, t) => Math.min(m, t.pnl as number), Infinity),
    streak,
  };
}

/** Per-calendar-day activity (YYYY-MM-DD, local TZ): trade count + net P&L.
 *  Includes EVERY trade, not just closed ones — open / practice trades show
 *  up via `count` (with pnl 0) so the user can see their journal activity on
 *  the calendar even before positions are closed. Date key prefers closedAt
 *  (a trade closed today belongs to today), falling back to openedAt. */
export function dailyPnl(trades: Trade[]): Map<string, { pnl: number; count: number }> {
  const map = new Map<string, { pnl: number; count: number }>();
  for (const t of trades) {
    const when = t.closedAt || t.openedAt;
    if (!when) continue;
    const d = new Date(when);
    if (isNaN(d.getTime())) continue;
    const key = `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, '0')}-${String(d.getDate()).padStart(2, '0')}`;
    const cur = map.get(key) ?? { pnl: 0, count: 0 };
    if (t.pnl != null) cur.pnl += t.pnl;
    cur.count += 1;
    map.set(key, cur);
  }
  return map;
}

/** Distinct tags across all trades, most-used first. */
export function allTags(trades: Trade[]): string[] {
  const counts = new Map<string, number>();
  for (const t of trades) for (const tag of t.tags) counts.set(tag, (counts.get(tag) ?? 0) + 1);
  return Array.from(counts.entries()).sort((a, b) => b[1] - a[1]).map(([t]) => t);
}

/* ----------------------------------------------------------------------- */
/*  CSV import                                                             */
/* ----------------------------------------------------------------------- */

/** Parse one delimited line, honouring double-quoted fields and "" escapes. */
function parseCsvLine(line: string, delim: string): string[] {
  const out: string[] = [];
  let cur = '';
  let inQuotes = false;
  for (let i = 0; i < line.length; i++) {
    const ch = line[i];
    if (inQuotes) {
      if (ch === '"') {
        if (line[i + 1] === '"') { cur += '"'; i++; }
        else inQuotes = false;
      } else {
        cur += ch;
      }
    } else if (ch === '"') {
      inQuotes = true;
    } else if (ch === delim) {
      out.push(cur);
      cur = '';
    } else {
      cur += ch;
    }
  }
  out.push(cur);
  return out.map((c) => c.trim());
}

type CsvField =
  | 'pair' | 'direction' | 'size' | 'entryPrice' | 'exitPrice'
  | 'pnl' | 'openedAt' | 'closedAt';

/** Header keyword → field, ordered so more specific matches win. */
const HEADER_MATCHERS: Array<{ field: CsvField; keys: string[] }> = [
  { field: 'closedAt',   keys: ['close time', 'closed', 'close date', 'closetime', 'exit time'] },
  { field: 'openedAt',   keys: ['open time', 'opened', 'open date', 'opentime'] },
  { field: 'exitPrice',  keys: ['close price', 'closeprice', 'exit price', 'exitprice', 'exit', 'close'] },
  { field: 'entryPrice', keys: ['open price', 'openprice', 'entry price', 'entryprice', 'entry', 'open'] },
  { field: 'pnl',        keys: ['profit', 'pnl', 'p&l', 'p/l', 'net', 'gain'] },
  { field: 'direction',  keys: ['type', 'side', 'direction', 'action'] },
  { field: 'size',       keys: ['volume', 'lots', 'size', 'qty', 'quantity'] },
  { field: 'pair',       keys: ['pair', 'symbol', 'instrument', 'ticker'] },
];

/** Best-effort field for a header cell, or null when nothing matches. */
function classifyHeader(headerRaw: string): CsvField | null {
  const h = headerRaw.toLowerCase().trim();
  if (!h) return null;
  for (const { field, keys } of HEADER_MATCHERS) {
    for (const k of keys) {
      if (h === k || h.includes(k)) return field;
    }
  }
  return null;
}

/** Parse a possibly-formatted number ("1 234,56", "$-120.00", "(50)"). */
function parseNum(raw: string): number | null {
  if (raw == null) return null;
  let s = raw.trim();
  if (!s) return null;
  let neg = false;
  if (/^\(.*\)$/.test(s)) { neg = true; s = s.slice(1, -1); }
  s = s.replace(/[^0-9.,+-]/g, '');
  // If both separators present, assume the last one is the decimal.
  if (s.includes(',') && s.includes('.')) {
    if (s.lastIndexOf(',') > s.lastIndexOf('.')) s = s.replace(/\./g, '').replace(',', '.');
    else s = s.replace(/,/g, '');
  } else if (s.includes(',')) {
    // A lone comma: decimal if it looks like one (≤2 trailing digits), else grouping.
    const after = s.slice(s.lastIndexOf(',') + 1);
    s = after.length > 0 && after.length <= 2 ? s.replace(',', '.') : s.replace(/,/g, '');
  }
  const n = parseFloat(s);
  if (!isFinite(n)) return null;
  return neg ? -n : n;
}

/** Normalise a date-ish cell to an ISO string, or null. */
function parseDate(raw: string): string | null {
  const s = (raw ?? '').trim();
  if (!s) return null;
  // MT5 commonly uses "YYYY.MM.DD HH:MM:SS" — swap dots in the date part.
  const mt5 = s.match(/^(\d{4})[.\-/](\d{1,2})[.\-/](\d{1,2})(.*)$/);
  if (mt5) {
    const [, y, mo, d, rest] = mt5;
    const iso = `${y}-${mo.padStart(2, '0')}-${d.padStart(2, '0')}${rest ? 'T' + rest.trim() : ''}`;
    const dt = new Date(iso);
    if (!isNaN(dt.getTime())) return dt.toISOString();
  }
  const dt = new Date(s);
  if (!isNaN(dt.getTime())) return dt.toISOString();
  return null;
}

function parseDirection(raw: string): TradeDirection {
  const s = (raw ?? '').toLowerCase();
  if (s.includes('sell') || s.includes('short')) return 'short';
  return 'long';
}

/**
 * Parse a trade-history CSV export (e.g. a MetaTrader 5 deals report) into
 * NewTrade records. Auto-detects the delimiter, locates the header row, and
 * fuzzy-maps columns by name. Rows without at least a pair and an entry price
 * are skipped.
 */
export function parseTradesCsv(text: string): NewTrade[] {
  if (!text) return [];
  // Strip BOM and split into non-empty raw lines.
  const lines = text.replace(/^﻿/, '').split(/\r\n|\r|\n/).filter((l) => l.trim().length > 0);
  if (!lines.length) return [];

  // Pick the delimiter from the line that yields the most columns.
  const delims = [',', ';', '\t'];
  const delim = delims
    .map((d) => ({ d, score: Math.max(...lines.slice(0, 10).map((l) => parseCsvLine(l, d).length)) }))
    .sort((a, b) => b.score - a.score)[0].d;

  // Find the header row — the first line where ≥2 cells classify to a field
  // and one of them is the pair column.
  let headerIdx = -1;
  let colMap: Partial<Record<CsvField, number>> = {};
  for (let i = 0; i < Math.min(lines.length, 25); i++) {
    const cells = parseCsvLine(lines[i], delim);
    const map: Partial<Record<CsvField, number>> = {};
    cells.forEach((c, idx) => {
      const f = classifyHeader(c);
      if (f && map[f] === undefined) map[f] = idx;
    });
    const hits = Object.keys(map).length;
    if (hits >= 2 && map.pair !== undefined && map.entryPrice !== undefined) {
      headerIdx = i;
      colMap = map;
      break;
    }
  }
  if (headerIdx < 0) return [];

  const get = (cells: string[], f: CsvField): string => {
    const idx = colMap[f];
    return idx !== undefined && idx < cells.length ? cells[idx] : '';
  };

  const trades: NewTrade[] = [];
  for (let i = headerIdx + 1; i < lines.length; i++) {
    const cells = parseCsvLine(lines[i], delim);
    if (cells.every((c) => c === '')) continue;

    const pair = get(cells, 'pair').trim();
    const entryPrice = parseNum(get(cells, 'entryPrice'));
    if (!pair || entryPrice == null) continue;

    const pnl = parseNum(get(cells, 'pnl'));
    const closedAt = parseDate(get(cells, 'closedAt'));
    const openedAt = parseDate(get(cells, 'openedAt'));

    trades.push({
      kind: 'real',
      pair: pair.toUpperCase(),
      direction: parseDirection(get(cells, 'direction')),
      entryPrice,
      exitPrice: parseNum(get(cells, 'exitPrice')),
      size: parseNum(get(cells, 'size')) ?? 1,
      pnl,
      rMultiple: null,
      openedAt: openedAt ?? closedAt ?? new Date().toISOString(),
      closedAt: pnl != null ? (closedAt ?? openedAt) : closedAt,
      tags: [],
      rating: 0,
      note: '',
      screenshot: null,
    });
  }
  return trades;
}

/* ------------------------------------------------------------------------- */
/*  Journal settings — goals + risk appetite                                  */
/* ------------------------------------------------------------------------- */

export type RiskAppetite = 'conservative' | 'moderate' | 'aggressive';
export type GoalKind = 'currency' | 'percent';

export interface JournalSettings {
  /** Starting account balance, in account currency. Used to compute current
   *  equity (= accountSize + sum of closed PnL) and the absolute target
   *  amount for percent-style goals. */
  accountSize: number;
  goal: {
    kind: GoalKind;
    /** For kind='currency' this is the target equity in dollars (e.g. 20000
     *  meaning "reach a $20k account"). For kind='percent' it's the percent
     *  growth from starting accountSize (e.g. 20 meaning "+20%"). No deadline
     *  — progress is shown as %-complete, not pace-against-time. */
    value: number;
  };
  risk: {
    appetite: RiskAppetite;
    /** Max % of account the user wants to risk per single trade. */
    maxRiskPerTradePct: number;
    /** Max % of account they want to allow drawing down in a single day. */
    maxDailyDrawdownPct: number;
  };
}

/** Presets — keep them sane, traders bias hot when picking ranges. */
export const RISK_PRESETS: Record<RiskAppetite, { maxRiskPerTradePct: number; maxDailyDrawdownPct: number; label: string; blurb: string }> = {
  conservative: { maxRiskPerTradePct: 0.5, maxDailyDrawdownPct: 3, label: 'Conservative',
    blurb: '0.5% per trade · 3% daily drawdown cap. Capital preservation first.' },
  moderate:     { maxRiskPerTradePct: 1.0, maxDailyDrawdownPct: 5, label: 'Moderate',
    blurb: '1% per trade · 5% daily drawdown cap. Industry-standard prop risk profile.' },
  aggressive:   { maxRiskPerTradePct: 2.0, maxDailyDrawdownPct: 8, label: 'Aggressive',
    blurb: '2% per trade · 8% daily drawdown cap. Faster growth, larger swings.' },
};

// (settings key now produced via settingsKey() — kept under telos:journal:settings:v1:<userId>)
export const SETTINGS_EVENT = 'telos:journal:settings-changed';

const DEFAULT_SETTINGS: JournalSettings = {
  accountSize: 10_000,
  goal: { kind: 'percent', value: 20 },
  risk: {
    appetite: 'moderate',
    maxRiskPerTradePct: RISK_PRESETS.moderate.maxRiskPerTradePct,
    maxDailyDrawdownPct: RISK_PRESETS.moderate.maxDailyDrawdownPct,
  },
};

export function readJournalSettings(): JournalSettings {
  try {
    const raw = localStorage.getItem(settingsKey());
    if (!raw) return DEFAULT_SETTINGS;
    const parsed = JSON.parse(raw) as Partial<JournalSettings>;
    return {
      accountSize: parsed.accountSize ?? DEFAULT_SETTINGS.accountSize,
      goal: { ...DEFAULT_SETTINGS.goal, ...(parsed.goal || {}) },
      risk: { ...DEFAULT_SETTINGS.risk, ...(parsed.risk || {}) },
    };
  } catch {
    return DEFAULT_SETTINGS;
  }
}

export function writeJournalSettings(s: JournalSettings) {
  localStorage.setItem(settingsKey(), JSON.stringify(s));
  window.dispatchEvent(new CustomEvent(SETTINGS_EVENT));
}

/* ------------------------------------------------------------------------- */
/*  KPIs — goal progress, risk reality, max drawdown, symbol & L/S splits    */
/* ------------------------------------------------------------------------- */

export interface GoalProgress {
  /** Cumulative closed P&L. */
  totalPnl: number;
  /** Current equity = accountSize + totalPnl. */
  currentEquity: number;
  /** Target equity in account currency. For kind=currency this is the
   *  goal.value verbatim; for kind=percent it's accountSize × (1 + value/100). */
  targetEquity: number;
  /** Equity gain still required to hit target (can go negative once achieved). */
  remainingAmount: number;
  /** 0–1, clamped at the upper end (progress past target stays at 1). */
  progressFrac: number;
  /** True once currentEquity >= targetEquity. */
  achieved: boolean;
}

export function computeGoalProgress(trades: Trade[], settings: JournalSettings): GoalProgress {
  const totalPnl = trades
    .filter((t) => t.closedAt && t.pnl != null)
    .reduce((a, t) => a + (t.pnl as number), 0);
  const currentEquity = settings.accountSize + totalPnl;
  const targetEquity = settings.goal.kind === 'currency'
    ? settings.goal.value
    : settings.accountSize * (1 + (settings.goal.value / 100));
  const remainingAmount = targetEquity - currentEquity;
  const denom = Math.max(0.0001, targetEquity - settings.accountSize);
  const progressFrac = Math.max(0, Math.min(1, totalPnl / denom));
  return {
    totalPnl,
    currentEquity,
    targetEquity,
    remainingAmount,
    progressFrac,
    achieved: currentEquity >= targetEquity,
  };
}


export interface RiskReality {
  /** Trades for which we could compute a $ risk (rMultiple present and != 0). */
  measuredCount: number;
  avgRiskPct: number;
  maxRiskPct: number;
  /** How many trades exceeded the declared maxRiskPerTradePct. */
  overshootCount: number;
  /** 0–1 share of measured trades that overshot. */
  overshootFrac: number;
  /** Worst single-day cumulative loss as % of account. */
  worstDailyDrawdownPct: number;
  /** Whether worst daily DD breached the declared cap. */
  worstDayBreached: boolean;
}

export function computeRiskReality(trades: Trade[], settings: JournalSettings): RiskReality {
  const account = settings.accountSize || 1;
  // Per-trade risk %, derived two ways depending on what the trade carries:
  //   (a) rMultiple present   → intended risk = |pnl / rMultiple| / account
  //   (b) realised loss only  → realised risk = |pnl| / account  (for losses)
  // (b) is the fallback for MT5-imported trades that don't carry R because
  // S/L wasn't logged in the statement. It under-counts "intended risk"
  // by ignoring winners, but it's the honest measure of risk you actually
  // ate on the trades that went against you — which is what the user
  // cares about when their declared cap is X% and they're checking
  // whether reality matches intent.
  const measured: number[] = [];
  for (const t of trades) {
    if (t.pnl == null) continue;
    if (t.rMultiple != null && t.rMultiple !== 0) {
      measured.push(Math.abs((t.pnl as number) / (t.rMultiple as number)) / account * 100);
    } else if ((t.pnl as number) < 0) {
      measured.push(Math.abs(t.pnl as number) / account * 100);
    }
  }
  const avg = measured.length ? measured.reduce((a, b) => a + b, 0) / measured.length : 0;
  const max = measured.length ? Math.max(...measured) : 0;
  const overshoot = measured.filter((r) => r > settings.risk.maxRiskPerTradePct).length;

  // Daily aggregate losses
  const byDay = new Map<string, number>();
  for (const t of trades) {
    if (t.pnl == null || !t.closedAt) continue;
    const d = new Date(t.closedAt);
    if (isNaN(d.getTime())) continue;
    const key = d.toISOString().slice(0, 10);
    byDay.set(key, (byDay.get(key) ?? 0) + (t.pnl as number));
  }
  const worstDayPnl = Math.min(0, ...Array.from(byDay.values()));
  const worstDailyDrawdownPct = Math.abs(worstDayPnl) / account * 100;

  return {
    measuredCount: measured.length,
    avgRiskPct: avg,
    maxRiskPct: max,
    overshootCount: overshoot,
    overshootFrac: measured.length ? overshoot / measured.length : 0,
    worstDailyDrawdownPct,
    worstDayBreached: worstDailyDrawdownPct > settings.risk.maxDailyDrawdownPct,
  };
}

export interface SymbolSlice { pair: string; count: number; fraction: number; pnl: number; }
export function computeSymbolBreakdown(trades: Trade[]): SymbolSlice[] {
  const map = new Map<string, { count: number; pnl: number }>();
  for (const t of trades) {
    const key = (t.pair || 'OTHER').toUpperCase();
    const cur = map.get(key) ?? { count: 0, pnl: 0 };
    cur.count++;
    cur.pnl += t.pnl ?? 0;
    map.set(key, cur);
  }
  const total = trades.length || 1;
  return Array.from(map.entries())
    .map(([pair, v]) => ({ pair, count: v.count, pnl: v.pnl, fraction: v.count / total }))
    .sort((a, b) => b.count - a.count);
}

export interface DirectionSplit { longs: number; shorts: number; longFrac: number; shortFrac: number; }
export function computeDirectionSplit(trades: Trade[]): DirectionSplit {
  const longs = trades.filter((t) => t.direction === 'long').length;
  const shorts = trades.filter((t) => t.direction === 'short').length;
  const total = longs + shorts || 1;
  return { longs, shorts, longFrac: longs / total, shortFrac: shorts / total };
}

/** Maximum closed-trade drawdown as a % of account size. Walks closed trades
 *  chronologically and tracks running equity peak vs trough. */
export function computeMaxDrawdownPct(trades: Trade[], accountSize: number): number {
  const closed = trades
    .filter((t) => t.pnl != null && t.closedAt)
    .sort((a, b) => new Date(a.closedAt as string).getTime() - new Date(b.closedAt as string).getTime());
  if (!closed.length || accountSize <= 0) return 0;
  let eq = accountSize;
  let peak = accountSize;
  let maxDD = 0;
  for (const t of closed) {
    eq += t.pnl as number;
    if (eq > peak) peak = eq;
    const dd = peak > 0 ? (peak - eq) / peak * 100 : 0;
    if (dd > maxDD) maxDD = dd;
  }
  return maxDD;
}
